import { Button, ButtonWithLoader } from '@neo/index'
import { cn } from '@neo/lib/utils'
import { Trash2, TriangleAlert } from 'lucide-react'
import type { RCard } from 'v2/types/api/billing'
import { PaymentBrandIcons } from '../billing.config'
import {
  useDeleteCardMutation,
  useRedirectionUrlMutation,
  useUpdateDefaultCardMutation,
} from '../mutations'
import { CardState } from '../types'
import { getAccountId } from '../utils'

// Subset of RCard fields PaymentCard actually reads. Lets non-Stripe sources
// (e.g. Cashfree) build a minimal object without filling every RCard field.
// Mutation paths (delete/verify/makeDefault) only fire when card_mapping_id
// is a meaningful Stripe ID — Cashfree cards pass 0 and the gating checks
// (showDelete, card_mapping_id !== 0) keep those paths inert.
export type PaymentCardData = Pick<
  RCard,
  'card_brand' | 'last_four_digits' | 'state' | 'default' | 'card_mapping_id'
>

export const PaymentCard = ({
  showDelete = false,
  size = 28,
  enableDefault = false,
  card,
  variant = 'default',
  onDeleteClick,
  deleting = false,
  subLabel,
}: {
  card: PaymentCardData
  showDelete?: boolean
  size?: number
  enableDefault?: boolean
  variant?: 'default' | 'payment-methods' | 'recharge-modal'
  // Second line under the card number. In the recharge dialog the card row is a
  // payment-mode row like Netbanking or Bank Transfer, so it carries the same
  // title/subtitle shape — the number on top, the fee under it.
  subLabel?: string
  onDeleteClick?: (card: RCard) => void
  // External in-flight state for the trash-button spinner, used when the delete
  // runs through a page-level mutation (e.g. the Razorpay token delete) instead
  // of this card's internal useDeleteCardMutation.
  deleting?: boolean
}) => {
  const { mutate: getRedirectionUrl, isPending } = useRedirectionUrlMutation()
  const { mutate: deleteCard, isPending: internalDeleting } = useDeleteCardMutation()
  const isDeleting = internalDeleting || deleting
  const { mutate: updateDefaultCard, isPending: isUpdatingDefaultCard } =
    useUpdateDefaultCardMutation()
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center">
        {/* min-w-0 rather than shrink-0: the pill now lives in here, so this group
            has to be allowed to shrink for the number's truncate to ever engage. */}
        {/* gap-3 matches the row's own px-3 inset, so the space to the left of the
            logo and the space between logo and text are the same 12px. The other
            payment modes use the same 36px column + gap, which is what keeps all
            three rows' text on one left edge. */}
        <div className="flex items-center gap-3 min-w-0">
          {/* No tile of our own: every brand asset already draws its own white
              rounded card with a hairline stroke (see ic-visa.svg), so a wrapper
              box put a box inside a box. This is just the sized column that keeps
              the text beside it aligned — 36px in the recharge dialog, matching
              the other payment modes' icon column. */}
          <div
            className={cn(
              'flex shrink-0 items-center justify-center',
              variant === 'payment-methods' && 'h-12 w-12',
              variant === 'recharge-modal' && 'h-8 w-9',
              variant === 'default' && 'h-10 w-10',
            )}
          >
            <PaymentBrandIcons
              brand={card.card_brand}
              size={variant === 'payment-methods' ? 24 : size}
            />
          </div>
          <div
            className={cn(
              'text-xs-600 flex flex-col min-w-0',
              variant === 'recharge-modal' ? 'gap-0.5' : 'gap-2',
            )}
          >
            {/* No "Add New Card" branch here any more. The add-card CTA used to be
                faked as an RCard with `last_four_digits: 'Add New Card'` and
                rendered through this component; it is a real button now, on both
                the Payment methods page and in the picker, so nothing constructs
                that sentinel. Rendering a card is this component's only job. */}
            {/* Number + Default sit in one tight row, gap-2 against the row's own
                gap-3, so the pill reads as qualifying this card rather than as a
                separate column at the far edge. The number truncates and the pill
                holds its width, so a long value gives way instead of the status.
                Every variant, the recharge dialog included: out at the row's right
                edge it was level with the tile rather than with the card, so in a
                list of payment MODES it read as "card is the default mode" — which
                is not what the flag means. Beside the number the scope is the card,
                and the two read as one phrase. */}
            <div className="flex items-center gap-2 min-w-0">
              <span className="truncate">{`•••• •••• ${card.last_four_digits}`}</span>
              {card.default && (
                <span className="text-xs-600 bg-bg3 px-2 py-0.5 rounded-lg whitespace-nowrap shrink-0">
                  Default
                </span>
              )}
            </div>
            {subLabel && <span className="text-s-500 text-secondary truncate">{subLabel}</span>}
            {card.state === CardState.AUTHENTICATION_REQUIRED && (
              <div className="text-xs-400 flex gap-1 text-status-warning items-center">
                <TriangleAlert size={16} />
                Verification pending
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 flex-1 group">
          {card.state === CardState.EXPIRED && (
            <span className="text-xs-600 text-destructive px-2 py-1 rounded-lg whitespace-nowrap">
              Expired
            </span>
          )}
          {card.state === CardState.AUTHENTICATION_REQUIRED && variant === 'payment-methods' && (
            <Button
              variant="outline"
              disabled={isPending}
              className="text-xs-600 px-2 py-1 rounded-lg whitespace-nowrap text-secondary"
              onClick={() => {
                getRedirectionUrl({
                  cardMappingId: card.card_mapping_id,
                  accountId: getAccountId(),
                })
              }}
            >
              Verify now
            </Button>
          )}
          {enableDefault && card.state === CardState.ACTIVE && !card.default && (
            <ButtonWithLoader
              type="button"
              variant="ghost"
              loading={isUpdatingDefaultCard}
              className={`text-xs-600 bg-bg3 px-2 py-1 rounded-lg whitespace-nowrap invisible group-hover:visible ${isUpdatingDefaultCard ? 'cursor-not-allowed visible' : ''}`}
              onClick={() => {
                updateDefaultCard({ card_mapping_id: card.card_mapping_id })
              }}
            >
              Make this the default card for payments
            </ButtonWithLoader>
          )}
          {showDelete && card.card_mapping_id !== 0 && (
            <ButtonWithLoader
              variant="ghost"
              loading={isDeleting}
              className="p-1 hover:bg-transparent rounded-md disabled:text-primary"
              onClick={() => {
                if (onDeleteClick) {
                  // Stripe callers pass full RCard objects through this prop; the
                  // showDelete + card_mapping_id !== 0 gate above keeps Cashfree
                  // cards (which only carry PaymentCardData) out of this path.
                  onDeleteClick(card as RCard)
                } else {
                  deleteCard({ card_mapping_id: card.card_mapping_id })
                }
              }}
            >
              {!isDeleting && <Trash2 className="w-4 h-4 text-destructive" />}
            </ButtonWithLoader>
          )}
        </div>
      </div>
    </div>
  )
}
